import React, { useState, useEffect, KeyboardEvent } from "react";
import makeStyles from "@material-ui/styles/makeStyles";
import FormaDePagoSelector from "../../formaDePagoSelector/FormaDePagoSelector";
import { TextField, Theme } from "@material-ui/core";
import CBUInput from "~styled/input/CBUInput";
import isCBUValid from "~libs/CBUValidator";
import CallToActionButton from "~styled/callToActionButton/CallToActionButton";
import CreditCardInput from "~styled/input/CreditCardInput";
import {
  isCreditCardNumberValid,
  getCreditCardType,
} from "~libs/creditCardValidator";
import visaIcon from "~images/logos/visa-icon.png";
import mastercardIcon from "~images/logos/mastercard-icon.png";
import amexIcon from "~images/logos/amex-icon.png";
import dinersIcon from "~images/logos/diners-icon.png";
import Avatar from "@material-ui/core/Avatar";
import InputAdornment from "@material-ui/core/InputAdornment";

const useStyles = makeStyles((theme: Theme) => ({
  logoSize: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
}));

const FORMA_PAGO_STEP = "FORMA_PAGO_STEP";
const DATOS_PAGO_CBU_STEP = "DATOS_PAGO_CBU_STEP";
const DATOS_PAGO_TC_STEP = "DATOS_PAGO_TC_STEP";
const ENTER_KEY = 13;

interface FormaPagoStepperProps {
  onChange?: (data: {
    formaPago: FormaPagoOption;
    cbu?: string | null;
    tarjetaCredito?: string | null;
    marcaTarjetaCredito?: string | null;
  }) => void;
}

type FormaPagoOption = {
  value: string;
};

const FormaPagoStepper: React.FC<FormaPagoStepperProps> = ({ onChange }) => {
  const classes = useStyles();

  const [formaPago, setFormaPago] = useState<FormaPagoOption | null>(null);
  const [cbu, setCbu] = useState<string | null>(null);
  const [tarjetaCredito, setTarjetaCredito] = useState<string | null>(null);

  const creditCardType: string | null = getCreditCardType(tarjetaCredito);
  const cardTypeIcon = getCardIcon(creditCardType);

  const canContinueCBU = cbu && cbu.length > 0 && isCBUValid(cbu);
  const canContinueTC =
    tarjetaCredito &&
    tarjetaCredito?.length >= 13 &&
    tarjetaCredito?.length <= 19 &&
    (!creditCardType || isCreditCardNumberValid(tarjetaCredito));
  const canContinue = canContinueTC || canContinueCBU;

  useEffect(() => {
    if (formaPago?.value === "CuponPago" && onChange) onChange({ formaPago });
  }, [formaPago, onChange]);

  const handleConfirmChanges = () => {
    const allCompleted = formaPago && canContinue;
    if (allCompleted && onChange)
      onChange({
        formaPago,
        cbu,
        tarjetaCredito,
        marcaTarjetaCredito: creditCardType,
      });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.keyCode === ENTER_KEY && canContinue) handleConfirmChanges();
  };

  const step = getStep(formaPago?.value);
  return (
    <div style={{ minHeight: "90vh" }}>
      {step === FORMA_PAGO_STEP && (
        <FormaDePagoSelector
          onChange={(value: string) => {
            setFormaPago({ value });
          }}
        />
      )}
      <div style={{ paddingTop: 24 }}>
        {step === DATOS_PAGO_CBU_STEP && (
          <TextField
            autoFocus
            onFocus={(event) => event.target.select()}
            fullWidth
            onKeyDown={onKeyDown}
            value={cbu || ""}
            onChange={(event) => setCbu(event.target.value)}
            placeholder="Ej: 9999999999999999999999"
            InputProps={{
              inputComponent: CBUInput as any,
            }}
          />
        )}
        {step === DATOS_PAGO_TC_STEP && (
          <TextField
            fullWidth
            onKeyDown={onKeyDown}
            autoFocus
            onFocus={(event) => event.target.select()}
            type="tel"
            value={tarjetaCredito || ""}
            onChange={(event) => {
              const value = event.target.value.replace(/\s/g, "");
              setTarjetaCredito(value);
            }}
            placeholder="Ej: 1111 2222 3333 4444"
            InputProps={{
              inputComponent: CreditCardInput as any,
              startAdornment: (
                <InputAdornment position="start">
                  {creditCardType && (
                    <Avatar
                      alt={creditCardType}
                      src={cardTypeIcon || undefined}
                      className={classes.logoSize}
                    />
                  )}
                </InputAdornment>
              ),
            }}
          ></TextField>
        )}
        {(step === DATOS_PAGO_CBU_STEP || step === DATOS_PAGO_TC_STEP) && (
          <div style={{ paddingTop: 24 }}>
            <CallToActionButton
              disabled={!canContinue}
              label={"Continuar"}
              onClick={handleConfirmChanges}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FormaPagoStepper;

const getStep = (formaPago: string | undefined): string => {
  if (!formaPago) return FORMA_PAGO_STEP;
  if (formaPago === "DebitoConCBU") return DATOS_PAGO_CBU_STEP;
  if (formaPago === "DebitoConTarjetaCredito") return DATOS_PAGO_TC_STEP;
  return FORMA_PAGO_STEP;
};

const getCardIcon = (type: string | null): string | null => {
  if (type === "MasterCard") return mastercardIcon;
  if (type === "Visa") return visaIcon;
  if (type === "AmericanExpress") return amexIcon;
  if (type === "Diners") return dinersIcon;
  return null;
};
